import type { FC } from 'react';
import React, { useEffect, useState, useCallback } from 'react';
import { IntlProvider } from 'react-intl';
import { IntlProvider as IntlNextProvider } from 'react-intl-next';

import { getTranslation, loadTranslation, DEFAULT_LOCALE } from './translation';

/**
 * NOTE: This file is globally mocked in next/tools/config/jestGlobalMocks.js
 */
export const I18nProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
	const [forcedLocale, setForcedLocale] = useState(undefined);

	const { locale: unforcedLoadle, messages } = getTranslation();
	const locale = forcedLocale || unforcedLoadle;

	useEffect(() => {
		document.documentElement.lang = locale;
	}, [locale]);

	// Allow a quick dev override for locale
	if (process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const changeLocaleListener = useCallback(
			(event: any) => {
				if (typeof event.data !== 'object') return;
				const { eventType, locale } = event.data;
				if (eventType === 'force_locale') {
					void loadTranslation(undefined, locale).then(() => setForcedLocale(locale));
				}
			},
			[setForcedLocale],
		);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			window.addEventListener('message', changeLocaleListener);
			return () => window.removeEventListener('message', changeLocaleListener);
		}, [changeLocaleListener]);
	}

	return (
		<IntlProvider
			key={`intl-provider-${locale}-v5`}
			data-test-language={locale}
			locale={locale}
			defaultLocale={DEFAULT_LOCALE}
			messages={messages}
			textComponent="span"
		>
			<IntlNextProvider
				key={`intl-provider-${locale}-v5`}
				data-test-language={locale}
				locale={locale}
				defaultLocale={DEFAULT_LOCALE}
				messages={messages}
				textComponent="span"
			>
				{children}
			</IntlNextProvider>
		</IntlProvider>
	);
};
